import type { RuleSet, Rule, Combinator } from "../../../validation/form/types";

/**
 * Extracts question names from an expression string.
 * Expressions use the format ${questionName} to reference other questions.
 *
 * @param expression - The expression string to parse (e.g., "${email}" or "${field1} + ${field2}")
 * @returns Array of question names found in the expression
 *
 * @example
 * ```typescript
 * extractQuestionNamesFromExpression("${email}"); // ["email"]
 * extractQuestionNamesFromExpression("${field1} + ${field2}"); // ["field1", "field2"]
 * ```
 */
function extractQuestionNamesFromExpression(expression: string): string[] {
  // Match all occurrences of ${questionName}
  const matches = expression.matchAll(/\$\{([^}]+)\}/g);
  const questionNames: string[] = [];

  for (const match of matches) {
    const questionName = match[1].trim();
    if (questionName) {
      questionNames.push(questionName);
    }
  }

  return questionNames;
}

/**
 * Extracts the question IDs that a validation rule depends on.
 *
 * This function recursively traverses a validation rule tree and collects
 * all question IDs referenced in the rules. These IDs represent other questions
 * whose answers affect the validation of the current question.
 *
 * It extracts dependencies from:
 * - The rule's id field (the question being validated against)
 * - The rule's expression field (e.g., "${otherQuestion}")
 * - The rule's leftExpression field (e.g., "${field1} + ${field2}")
 *
 * @param rule - The validation rule or combinator to extract dependencies from
 * @returns Array of unique question IDs that this validation depends on
 *
 * @example
 * ```typescript
 * const rule = {
 *   combinator: "or",
 *   rules: [
 *     { id: "question1", operator: "eq", value: "yes" },
 *     { id: "question2", operator: "neq", value: "no" }
 *   ]
 * };
 * extractValidationDependencies(rule); // ["question1", "question2"]
 *
 * const expressionRule = {
 *   id: "confirm_email",
 *   operator: "eq",
 *   expression: "${email}",
 * };
 * extractValidationDependencies(expressionRule); // ["confirm_email", "email"]
 * ```
 */
export function extractValidationDependencies(rule: RuleSet): string[] {
  // If this is a combinator (has nested rules)
  if ("rules" in rule && Array.isArray(rule.rules)) {
    const dependencies = rule.rules.flatMap((nestedRule: Rule | Combinator) =>
      extractValidationDependencies(nestedRule as RuleSet),
    );
    // Return unique and sorted dependencies for consistent React dependency arrays
    return Array.from(new Set(dependencies)).sort();
  }

  const dependencies: string[] = [];

  // If this is a leaf rule (has an id)
  if ("id" in rule && typeof rule.id === "string") {
    dependencies.push(rule.id);
  }

  // Extract question names from expression and leftExpression fields
  type ExpressionField = "expression" | "leftExpression";
  const extractFromField = (field: ExpressionField) => {
    if (field in rule) {
      const value = rule[field as keyof typeof rule];
      if (typeof value === "string") {
        dependencies.push(...extractQuestionNamesFromExpression(value));
      }
    }
  };

  extractFromField("expression");
  extractFromField("leftExpression");

  // Return unique and sorted dependencies for consistent React dependency arrays
  return Array.from(new Set(dependencies)).sort();
}
